import React from 'react'
import { useTranslation } from 'react-i18next'
import { useReactiveVar } from '@apollo/client'
import { energyPerYearUnit, powerUnit } from '../../../util/formatters'
import { Provider } from '../../../calculators/providers/common/Provider'
import { formatReportParameter } from '../../../provider/provider-parameter-formatters'
import { ResultSubCategory } from '../../../provider/result-categories'
import { getFilteredResultsFromProviders } from '../../../calculators/calculator-result-utility'
import { mapNamedResultsToRows } from './report-utility'
import { ReportTable } from './ReportTable'
import { ProviderType } from '../../../generated/graphql'
import { dictionariesVar } from '../../../apollo/reactive-vars'

type Props = {
  providers: Provider[]
  subCategory: ResultSubCategory
}

export function ReportProvidersProvidedTable({
  providers,
  subCategory,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const { providerIdsToContainerNames } = useReactiveVar(dictionariesVar)

  const { energyType } = subCategory
  if (!energyType) {
    throw new Error('Providers provided table filter must include energy type.')
  }

  const filteredProviders = providers.filter((provider) =>
    provider.providedEnergyTypes.includes(energyType),
  )

  const resultByProvider = filteredProviders.map((provider) => {
    const containerName = providerIdsToContainerNames[provider.id]
    const resultTitle = containerName
      ? `${provider.name} (${containerName})`
      : provider.name
    return {
      name: resultTitle,
      result: getFilteredResultsFromProviders([provider], subCategory),
      provider,
      otherValues: [{ other: formatReportParameter(provider, energyType) }],
    }
  })

  const rows = mapNamedResultsToRows(resultByProvider, {
    totalRowName: t('common.total'),
    totalOtherValues: [{ omit: true }],
  })

  const headerCells = [
    { firstRow: energyPerYearUnit },
    { firstRow: powerUnit },
    { firstRow: t(`generalImportantParameterNameBy.energyType.${energyType}`) },
  ]

  const hybridHeatPumpExists = filteredProviders.some(
    ({ type }) => type === ProviderType.HybridHeatPump,
  )

  return (
    <ReportTable
      title={t(`providedEnergyNameBy.energyType.${energyType}`)}
      caption={
        hybridHeatPumpExists
          ? `* ${t(`report.providersProvidedCaption.${energyType}`)}`
          : undefined
      }
      headerCells={headerCells}
      rows={rows}
    />
  )
}
